import performanceMetaUtility from '@sparta.global.utilities/performance-meta-utility/dist/es6.js';
import spartaSingletonUtility from '@sparta.global.utilities/sparta-singleton-utility/dist/es6.js';
import spartaTransportUtility from '@sparta.global.utilities/sparta-transport-utility/dist/es6.js';
import spartaUILayersUtility from '@sparta.global.utilities/sparta-ui-layers-utility/dist/es6.js';
import $ from '@sparta.global.vendors/jquery';
import Handlebars from '@sparta.global.vendors/handlebars';

/* eslint-disable import/prefer-default-export */

const overridePropertyMapData = {};
const overrideDetailMapData = {};

/**
 * Utility sb-credit-card-utility
 * @description Site-specific utility for the sb-credit-cards site&#x27;s product pages
 * @copyright 2018-1-19
 * @file
 */

/* global spaParams */

/**
 * @const EVENTS
 * @description constant for storing namespaced event strings
 */
const EVENTS = {
  // Show error with homepage link
  errorShowHome: 'errorShowHome',
  // Show error with credit card overview link
  errorShowGeneric: 'errorShowGeneric',
  // Show loader
  loaderShow: 'loaderShow',
  // Hide loader
  loaderHide: 'loaderHide',
  // Card list ajax and render complete
  cardListReady: 'cardListReady',
  // Card list filtering
  cardListFilter: 'cardListFilter',
  // Card list count
  cardListCount: 'cardListCount',
  // For add and remove compare
  compareUpdate: 'compareUpdate',
  // Publish compare data
  compareData: 'compareData',
  // For add compare table from modal
  compareTableUpdate: 'compareTableUpdate',
  // For add compare table ready
  compareTableReady: 'compareTableReady',
  // For clearing zip code cookie
  clearZipCodeCookie: 'clearZipCodeCookie',
  // Product details ready
  productDetailsReady: 'productDetailsReady',
  // Product details rendered
  productDetailsRendered: 'productDetailsRendered',
  // Quiz ready
  quizReady: 'quizReady',
  // Carousel ready
  carouselReady: 'carouselReady',
  // When card is removed
  cardRemoved: 'cardRemoved',
  // Add card layer opened
  addCardLayerOpen: 'addCardLayerOpen',
  valueChange: 'valueChange'
};

/**
 * Equivalent of _.isObject
 * @param {any} value The value to assert.
 * @returns {boolean} True if the given value is an object.
 */
const isObject = value => value && typeof value === 'object';

/**
 * Equivalent of _.map
 * @param {object} object The object ot map.
 * @param {function} callback The callback to invoke for each member of 'object'.
 * @returns {Array} An array containing the mapped values.
 */
const map = (object, callback) => {
  const results = [];
  Object.keys(object || {}).forEach(key => results.push(callback(object[key], key, object)));
  return results;
};

/**
 * Converts an object from key/value pairs to a query string. For example:
 * { foo: 'bar', baz: 'quxx' } would be translated to '?foo=bar&baz=quxx'.
 * @param {any} object The object to convert to a query string.
 * @returns {string} The formatted query string.
 */
function stringify(object) {
  if (!isObject(object)) return '';
  const onValue = (value, key) => {
    const serialized = isObject(value) ? JSON.stringify(value) : value;
    return `${key}=${encodeURIComponent(serialized)}`;
  };
  return map(object, onValue).join('&');
}

/**
 * @class SBCreditCardUtility
 */
class SBCreditCardUtility {
  /**
   * constructor
   *
   * @description Only one instance of this utility is available since it's a singleton.
   */
  constructor() {
    this.Transport = spartaTransportUtility;
  }

  /**
   * @property {Object} EVENTS exposes event name constant
   * @description getter for EVENTS constant
   *
   * @returns {Object} EVENTS object
   */
  get EVENTS() {
    // eslint-disable-line
    return EVENTS;
  }

  /**
   * categoryMapping
   * @description Used for marketing tracking
   *
   * @returns {void}
   */
  categoryMapping() {
    // eslint-disable-line
    return {
      cash: 'cash-rewards',
      travel: 'travel-rewards',
      popular: 'popular-cards',
      lowInterest: 'low-interest',
      buildCredit: 'build-credit'
    };
  }

  /**
   * Opens a popup link
   *
   * @param {Object} e click Event
   * @param {String} modalName name of the modal
   *
   * @returns {void}
   */
  openPopupLink(e = {
    target: ''
  }, modalName = '') {
    spartaUILayersUtility.displayPopup(e.target.href, modalName, 'left=50,top=50,width=1100,height=750,scrollbars,status=1,resizable=1');
  }

  /**
   * Service request to obtain cards products.
   *
   * @param {Object} params object containing parameters to pass as a query string.
   * @param {Array} campaigns List of campaigns to retrieve properties
   * @param {Array} properties List of properties required for campaign
   *
   * @returns {Promise} Promise containing response from endpoint
   */
  productPropertiesMap(params, campaigns = [], properties = []) {
    // Check if we have campaigns first - if not return promise
    // Since spanish jcr doesn't follow the same way en does
    // can eventually go back and make them common.
    if (!campaigns.length) {
      return new Promise(resolve => {
        // eslint-disable-next-line no-console
        console.warn('No campaigns requested.');
        resolve({});
      });
    }
    const {
      siteParams
    } = spaParams;
    const queryString = params ? `?${stringify(params)}` : '';
    const host = `${siteParams.env === 'local' ? siteParams.aps2endpoint_local : siteParams.aps2endpoint}`;
    const url = `${host}/salesservices/productPropertiesMap${queryString}`;
    const isSmallBusiness = true;
    // Sparta 6.0.2-beta.11 is not populating the lang attribute correctly.
    // const locale =
    // document.getElementsByTagName('html')[0].getAttribute('lang').replace('-', '_') || 'en_US';
    const locale = `${spaParams.language}_US`;
    const payload = JSON.stringify({
      locale,
      isSmallBusiness,
      campaigns,
      properties
    });
    return new Promise((resolve, reject) => {
      const obj = {
        url,
        method: 'POST',
        contentType: 'application/json',
        dataType: 'json',
        error: reject,
        data: payload,
        success: response => {
          // If PCM Override enabled, merge override data over PCM response
          if (siteParams.pcmOverrideEnabled) {
            resolve({
              ...response,
              ...overridePropertyMapData
            });
          }
          resolve(response);
        },
        cache: true
      };
      this.Transport.subscribe(obj);
    });
  }

  /**
   * Service request to obtain all properties for a given campaign
   * @param {Object} params - object containing the parameters to pass as a query string.
   * Needs to contain campaignID, poCode, and locale
   *
   * @returns {Promise} Promise containing response from endpoint
   */
  productProperties(params) {
    const {
      siteParams,
      pageParams
    } = spaParams;
    const url = `${siteParams.env === 'local' ? siteParams.aps2endpoint_local : siteParams.aps2endpoint}/salesservices/productProperties`;
    return new Promise((resolve, reject) => {
      const obj = {
        url,
        method: 'GET',
        dataType: 'json',
        data: params,
        error: reject,
        success: response => {
          // If PCM Override enabled, merge data from override file
          if (siteParams.pcmOverrideEnabled) {
            const overrideData = overridePropertyMapData[params.locale][pageParams.pageID];
            resolve({
              ...response,
              ...overrideData
            });
          }
          resolve(response);
        },
        cache: true
      };
      this.Transport.subscribe(obj);
    });
  }

  /**
   * @param {Object} params - object containing the parameters used for the payload
   *
   * @returns {Promise} Promise containing response from endpoint
   */
  productDetailMap(params) {
    const {
      siteParams
    } = spaParams;
    const url = `${siteParams.env === 'local' ? siteParams.aps2endpoint_local : siteParams.aps2endpoint}/salesservices/productDetailMap`;
    const payload = JSON.stringify(params);
    return new Promise((resolve, reject) => {
      const obj = {
        url,
        method: 'POST',
        contentType: 'application/json',
        dataType: 'json',
        data: payload,
        error: reject,
        success: response => {
          // If PCM Override enabled, merge override data over PCM response
          if (siteParams.pcmOverrideEnabled) {
            resolve({
              ...response,
              ...overrideDetailMapData
            });
          }
          resolve(response);
        },
        cache: true
      };
      this.Transport.subscribe(obj);
    });
  }

  /**
   * Prefetch product and apply pages of card element in list/category view.
   * In list/category view, only welcome.go page and `Learn More` can be prefetched
   * initialize.go sets cookies and can't be prefetched on pages with more
   * than one `Apply Now` link
   * @param {Object} cardEl Card DOM element.
   * @returns {void}
   */
  prefetchCard(cardEl) {
    if (!cardEl || spaParams.siteParams.disablePerformanceMeta) return;
    const isAlreadyPrefetched = cardEl.classList.contains('spa-prefetch');
    if (isAlreadyPrefetched) return;
    const learnLinkEl = cardEl.querySelector('a.learn');
    const prefetchUrl = href => performanceMetaUtility.addMetaFromObject({
      link: [{
        rel: 'prefetch',
        href
      }]
    });
    const learnLinkHref = learnLinkEl && learnLinkEl.getAttribute('href');
    if (learnLinkHref) prefetchUrl(learnLinkHref);
  }

  /**
  * Delegate multiple event listeners to the module container so
  * dom re-rendering will not double bind etc.
  *
  * @param {String} events - comma separated events to listen for
  * @param {String} selector - selector to delegate to target
  * @param {Function} handler - handler for event being listened for
  * @param {Boolean} once - if true, calls event handler only once
  * @param {HTMLElement} context - HTML Element context
  * @returns {void}
  */
  delegateEvents(events, selector, handler, once, context) {
    const eventsArray = events.split(',').map(item => item.trim());
    if (eventsArray.length > 1) {
      eventsArray.forEach(eventName => {
        this.delegateEvent(eventName, selector, handler, once, context);
      });
    } else {
      this.delegateEvent(events, selector, handler, once, context);
    }
  }

  /**
   * Delegate event listeners to the module container so dom re-rendering will not double bind etc.
   *
   * @param {String} event - event to listen for
   * @param {String} selector - selector to delegate to target
   * @param {Function} handler - handler for event being listened for
   * @param {Boolean} once - if true, calls event handler only once
   * @param {HTMLElement} context - HTML Element context
   * @returns {void}
   */
  delegateEvent(event, selector, handler, once, context) {
    context.addEventListener(event, function delegateTarget(e) {
      for (let {
        target
      } = e; target && target !== this; target = target.parentNode) {
        if (target && target.matches(selector)) {
          if (once) {
            if (e.currentTarget.dataset.triggered) return;
            e.currentTarget.dataset.triggered = true;
          }
          handler.call(target, e);
          break;
        }
      }
    }, false);
  }

  /**
     * parseFootnotes
     *
     * @description Scans DOM for unprocessed footnotes served from PCM &
     * manipulates them so they can be rendered
     * @param {Object} campaignProperties - the campaignProperties served from PCM
     * @returns {void}
     */
  parseFootnotes(campaignProperties) {
    // scan the entire page for footnotes populated from PCM.
    const footnotes = $('[data-footnote-jrn]');
    if (footnotes.length) {
      footnotes.each(function buildRef() {
        this.innerHTML = '<span class="spa-footnote-order"></span>';
        const $this = $(this);
        const footnoteRef = $this.data('footnote-jrn');
        const footnoteContent = Handlebars.Utils.escapeExpression(campaignProperties[footnoteRef]);
        $this.removeAttr('data-footnote-jrn');
        let footnoteLink = `<a href="#ftn_${footnoteRef}_content" id="ftn_${footnoteRef}" data-footnote-content="${footnoteContent}" data-footnote="${footnoteRef}">`;
        footnoteLink += this.getElemHtml($this);
        footnoteLink += '</a>';
        $this.html(footnoteLink);
      });
      this.spartaFootnote.rebuildFootnotes();
    }
  }

  /**
     * Generates content for template partial
     * @param {Object} content - JCR content
     * @param {Object} data - Data from backend for building content
     * @returns {Object} parsed content
     */
  generateLinkContent(content, data) {
    return JSON.parse(Handlebars.compile(JSON.stringify(content))(data).replace(/\n/g, '\\n'));
  }

  /**
     * generateCardCatalog
     *
     * @description Obtains marketing codes for tracking parameters
     * used by smallbusiness apply now link query string.
     * @param {String} campaignId - campaignId of this card
     * @param {Array} cardCatalogs - array of catalogs for card
     * @param {Array} params - array of module params
     * @param {Array} category - category array
     * @returns {Object} marketingCode and marketingTrack
     */
  generateCardCatalog(campaignId, cardCatalogs, params, category) {
    const campaignIdNum = campaignId.split('~')[0];
    const catalog = cardCatalogs.find(cat => cat.campaignId === campaignIdNum) || {};

    // create campaign string that new API uses
    const concatCampaign = `${catalog.campaignId}~${catalog.pocode}~en_US`;
    const {
      marketingCode
    } = catalog;
    const categoryMapping = this.categoryMapping();
    let pageId = spaParams.pageParams.pageID;
    if (params.requestParams && params.requestParams.failquiet === 'allCards' && location.hash === '#filter') {
      pageId = 'view-all-cards';
    }
    let marketingTrack;
    // see if the created campaign string is equal to one in the catalogs
    if (campaignId === concatCampaign && params.filter && params.filter.category) {
      // assign marketing track code for overview page
      marketingTrack = catalog.marketingTrack[pageId][categoryMapping[params.filter.category[0]]];
    }

    // Category based off category
    if (marketingTrack === undefined && catalog.marketingTrack && category) {
      marketingTrack = catalog.marketingTrack[pageId][categoryMapping[category]];
    }

    // Category based off category page
    if (marketingTrack === undefined && catalog.marketingTrack) {
      marketingTrack = catalog.marketingTrack[pageId][spaParams.pageParams.category];
    }
    return {
      marketingCode,
      marketingTrack
    };
  }

  /**
   * Obtains elements  as a safe function
   * @param {HTMLElement} $elem jquery element
   * @returns {string} element html
   */
  static getElemHtml($elem) {
    return $elem.html();
  }
}

/**
 * @const sbCreditCardUtility
 * @description single instance of SBCreditCardUtility used to expose this utility
 */
const sbCreditCardUtility = spartaSingletonUtility('sparta.utility.sbCreditCardUtility', SBCreditCardUtility);

export { sbCreditCardUtility as default };
